import { m } from "@nerves/i18n/messages";
import type { DeliveryPrefs, MeView } from "@nerves/shared";
import { DiscordLogo, IdentificationCard } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { Page, Panel } from "@/components/shell/Page";
import { Badge, Button, Callout, InlineError, SectionHeader, Switch } from "@/components/ui";
import { api } from "@/lib/api";
import { authConfigQuery, keys, useMe } from "@/lib/queries";
import { toast } from "@/lib/toast";

interface ConnectionsSearch {
  error?: string;
}

export const Route = createFileRoute("/_app/connections")({
  validateSearch: (s: Record<string, unknown>): ConnectionsSearch => ({
    error: typeof s.error === "string" ? s.error : undefined,
  }),
  component: ConnectionsPage,
});

function ConnectionCard({
  icon: I,
  name,
  body,
  linked,
  detail,
  action,
}: {
  icon: typeof DiscordLogo;
  name: string;
  body: string;
  linked: boolean;
  detail?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-start gap-3 border-b border-line p-4 last:border-b-0">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-control border border-line bg-canvas text-fg-muted">
        <I size={18} aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold">{name}</h3>
          <Badge tone={linked ? "success" : "neutral"}>{linked ? m.conn_linked() : m.conn_not_linked()}</Badge>
        </div>
        <p className="text-sm text-fg-muted text-pretty">{body}</p>
        {detail}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </li>
  );
}

function DiscordAction({ me, enabled }: { me: MeView; enabled: boolean }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const unlink = useMutation({
    mutationFn: () => api.post("/api/auth/unlink-account", { providerId: "discord" }),
    onSuccess: () => {
      toast.show({ title: m.conn_discord_unlinked(), tone: "success" });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
  if (!enabled) return null;
  if (me.connections.discord.linked) {
    return (
      <Button size="sm" variant="ghost" isPending={unlink.isPending} onPress={() => unlink.mutate()}>
        {m.conn_discord_unlink()}
      </Button>
    );
  }
  return (
    <Button
      variant="primary"
      size="sm"
      isPending={pending}
      onPress={async () => {
        setPending(true);
        try {
          const res = await api.post<{ url?: string }>("/api/auth/link-social", {
            provider: "discord",
            callbackURL: "/connections",
            errorCallbackURL: "/connections?error=link",
          });
          if (res.url) window.location.assign(res.url);
        } catch {
          toast.show({ title: m.conn_link_failed(), tone: "danger" });
        } finally {
          setPending(false);
        }
      }}
    >
      {m.conn_discord_link()}
    </Button>
  );
}

function Prefs({ prefs }: { prefs: DeliveryPrefs }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (patch: Partial<DeliveryPrefs>) => api.patch<DeliveryPrefs>("/api/v1/me/prefs", patch),
    onMutate: (patch) => {
      qc.setQueryData<MeView | null>(keys.me, (me) => (me ? { ...me, prefs: { ...me.prefs, ...patch } } : me));
    },
    onError: () => void qc.invalidateQueries({ queryKey: keys.me }),
    onSuccess: () => toast.show({ title: m.prefs_saved(), tone: "success" }),
  });
  return (
    <section aria-labelledby="prefs-title" className="flex flex-col gap-3">
      <SectionHeader id="prefs-title" title={m.prefs_title()} />
      <Panel className="flex flex-col gap-4 p-4">
        <Switch isSelected={prefs.pingOnQuestion} onChange={(v) => save.mutate({ pingOnQuestion: v })}>
          {m.prefs_question()}
        </Switch>
        <Switch
          isSelected={prefs.pingOnNote}
          onChange={(v) => save.mutate({ pingOnNote: v })}
          description={m.prefs_note_desc()}
        >
          {m.prefs_note()}
        </Switch>
        {save.isError ? <InlineError message={m.error_generic()} /> : null}
      </Panel>
    </section>
  );
}

function ConnectionsPage() {
  const me = useMe().data;
  const config = useQuery(authConfigQuery).data;
  const search = Route.useSearch();
  if (!me) return null;
  const discord = me.connections.discord;
  return (
    <Page title={m.connections_title()} intro={m.connections_intro()}>
      {search.error ? (
        <Callout tone="danger" role="alert">
          {m.conn_link_failed()}
        </Callout>
      ) : null}
      <Panel>
        <ul>
          <ConnectionCard
            icon={IdentificationCard}
            name="Cyrellian"
            body={m.conn_cyrellian_body()}
            linked={me.connections.cyrellian.linked}
            detail={<p className="text-xs text-fg-subtle">{me.email}</p>}
          />
          <ConnectionCard
            icon={DiscordLogo}
            name="Discord"
            body={config?.discordLink === false ? m.conn_discord_not_configured() : m.conn_discord_body()}
            linked={discord.linked}
            detail={
              discord.linked && discord.username ? (
                <p className="text-xs text-fg-subtle">{m.conn_discord_as({ name: discord.username })}</p>
              ) : null
            }
            action={<DiscordAction me={me} enabled={config?.discordLink ?? false} />}
          />
        </ul>
      </Panel>
      <Prefs prefs={me.prefs} />
    </Page>
  );
}
